/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { Stack, Typography, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetFirebaseDealCountQuery } from '@/features/deals/dealsApi';
import { usePipelineContext } from '@/context/pipelineContext';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';

export const DealTableCounterCard = ({ title, countType = 'inProgressDealAmount', pipelineId }) => {
	const { filters, owners } = useInstantSearchOwnerFilters();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails, isFetching: isUserFetching } = useGetUserDetailsQuery(
		{ email },
		{ skip: !email },
	);

	const { activeStages, active, completed, endorsements } = usePipelineContext(pipelineId);

	const dealStages = useMemo(() => {
		const invoicedIds = completed.filter((id) => !endorsements.includes(id));
		const inProgressIds = active.filter(
			(id) => !completed.includes(id) && !endorsements.includes(id),
		);

		if (countType === 'invoicedDealAmount') return invoicedIds;
		if (countType === 'totalDealAmount') return [...inProgressIds, ...invoicedIds];
		return inProgressIds;
	}, [countType, active, completed, endorsements]);

	const rates = useMemo(() => {
		const newBusinessStages = activeStages
			.filter((p) => p.label !== 'Endorsement in Progress')
			.sort((a, b) => a.order - b.order);

		const defaultRates = [5, 15, 20, 30, 80];

		const conversionRates = Object.fromEntries(
			newBusinessStages.map((stage) => [stage.id, defaultRates[stage.order] ?? 0]),
		);

		return conversionRates;
	}, [activeStages]);

	const { data, isFetching, isError } = useGetFirebaseDealCountQuery(
		{
			filters,
			countType,
			userIds: owners,
			dealStages,
			pipelineId,
			conversionRates: userDetails?.broker?.conversionRates || rates,
		},
		{ skip: !userDetails || !pipelineId },
	);
	const isLoading = isUserFetching || isFetching;
	return (
		<DealTableCounterCardWrapper
			title={title}
			data={data}
			isLoading={isLoading}
			isError={isError}
		/>
	);
};

export const DealTableCounterCardWrapper = ({ title, data, isLoading, isError }) => {
	return (
		<Stack
			height='100%'
			justifyContent='center'
			alignItems='center'
			sx={{ px: { xs: 0.5, sm: 1 }, textAlign: 'center' }}
		>
			<Typography
				sx={{
					fontSize: { xs: '10px', sm: '11px' },
					lineHeight: 1.3,
					px: 0.5,
				}}
			>
				{title}
			</Typography>
			<Stack
				direction='column'
				justifyContent='center'
				alignItems='center'
				sx={{ height: { xs: 48, sm: 60 }, minWidth: 0, width: '100%' }}
			>
				{isLoading ? (
					<Skeleton variant='text' height={40} width={60} />
				) : isError ? (
					<Typography color='error' sx={{ fontSize: { xs: '10px', sm: '12px' } }}>
						Failed to retrieve count
					</Typography>
				) : (
					<>
						<Typography
							variant='card_h4'
							sx={{
								fontSize: { xs: '16px', sm: '22px' },
								lineHeight: 1.2,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								maxWidth: '100%',
							}}
						>
							{data?.countTotal}
						</Typography>
						<Typography
							variant='card_header'
							sx={{
								fontSize: { xs: '9px', sm: '11px' },
								lineHeight: 1.2,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								maxWidth: '100%',
							}}
						>
							{`${data?.countAverage} average per deal`}
						</Typography>
					</>
				)}
			</Stack>
		</Stack>
	);
};
